import { Callout } from "@bednbody/ui/components/callout";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Spinner } from "@bednbody/ui/components/spinner";
import { Textarea } from "@bednbody/ui/components/textarea";
import { cn } from "@bednbody/ui/lib/utils";
import { WarningOctagonIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useAssignableRiders } from "../rider-sessions.hooks";

/** CORE-05B §3.2's fixed periods, shown so nobody has to guess what a code means. */
const CODES = [
  ["A", "Session A", "2:00 PM – 12:00 AM"],
  ["B", "Session B", "3:00 PM – 1:00 AM"],
  ["C", "Session C", "4:00 PM – 2:00 AM"],
] as const;

const EXTENSION_CHOICES = [1, 2, 3] as const;

interface EmergencySessionDialogProps {
  open: boolean;
  hubId: string | null;
  hubName: string | null;
  serviceDate: string;
  isPending: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (input: {
    serviceDate: string;
    code: "A" | "B" | "C";
    toRiderId: string;
    maxExtensionHours: 1 | 2 | 3;
    reason: string;
  }) => void;
}

/**
 * OQ-122 — create a Rider Session that generation never produced.
 *
 * ═══ THIS DIALOG IS DELIBERATELY UNCOMFORTABLE ═══
 *
 * Every other override in this app moves something that already exists. This one
 * manufactures supply, and CORE-05B §2 forbids it in writing: "Admin or Hub
 * Manager must not manually create extra supply outside the approved generation
 * rules." It exists because the client overruled that page, not because the page
 * was misread.
 *
 * So the banner is destructive-toned rather than a warning, the button says what
 * it does rather than "Save", and the copy states the two facts an operator
 * cannot see from anywhere else:
 *
 *   · the Session is marked as manual FOREVER and is excluded from every supply
 *     count — because `run_payroll` reads what it earns, and "generated or
 *     typed?" has to stay answerable;
 *   · a Rider is REQUIRED. Unowned emergency supply would publish an anonymous
 *     extra slot into the pool, which is the widest possible reading of an
 *     authority that already contradicts an approved page.
 */
export function EmergencySessionDialog({
  open,
  hubId,
  hubName,
  serviceDate,
  isPending,
  errorMessage,
  onClose,
  onSubmit,
}: EmergencySessionDialogProps) {
  const [date, setDate] = useState(serviceDate);
  const [code, setCode] = useState<"A" | "B" | "C">("A");
  const [riderId, setRiderId] = useState("");
  const [extension, setExtension] = useState<1 | 2 | 3>(2);
  const [reason, setReason] = useState("");

  const { data: riders, isLoading: ridersLoading } = useAssignableRiders(hubId);

  useEffect(() => {
    if (open) {
      setDate(serviceDate);
      setCode("A");
      setRiderId("");
      setExtension(2);
      setReason("");
    }
  }, [open, serviceDate]);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && riderId !== "" && date !== "" && !isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create a Session outside generation</DialogTitle>
          <DialogDescription>
            {hubName ? `${hubName} · ` : ""}for a named backup Rider
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Stated before any field, because it changes whether the operator
              should be here at all. */}
          <p className="flex gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <WarningOctagonIcon weight="fill" className="mt-px size-4 shrink-0" />
            <span>
              Rider Sessions are generated once a week from a locked Therapist count. Creating one
              by hand is outside that rule and exists only by client decision (OQ-122). Use it when
              supply failed and a backup is standing there — not to reshape a week.
            </span>
          </p>

          {hubId === null ? (
            <p className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
              Choose a single Dispatch Area first. A Session belongs to exactly one.
            </p>
          ) : null}

          <div className="space-y-2">
            <label htmlFor="emergency-date" className="block text-sm font-medium">
              Service date
            </label>
            <Input
              id="emergency-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <fieldset>
            <legend className="mb-2 block text-sm font-medium">Session period</legend>
            <RadioGroup value={code} onValueChange={(v) => setCode(v as "A" | "B" | "C")}>
              <div className="space-y-1.5">
                {CODES.map(([value, label, window]) => (
                  <label
                    key={value}
                    htmlFor={`code-${value}`}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors",
                      code === value ? "border-primary bg-primary/5" : "border-border",
                    )}
                  >
                    <RadioGroupItem id={`code-${value}`} value={value} />
                    <span className="flex-1 text-sm font-medium">{label}</span>
                    <span className="text-xs text-muted-foreground">{window}</span>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </fieldset>

          <fieldset>
            <legend className="mb-2 block text-sm font-medium">Assign to</legend>
            {ridersLoading ? (
              <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
                <Spinner className="size-4" />
                Loading riders in this hub…
              </div>
            ) : (riders ?? []).length === 0 ? (
              <p className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
                No active Rider in this Dispatch Area.
              </p>
            ) : (
              <RadioGroup value={riderId} onValueChange={setRiderId}>
                <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                  {(riders ?? []).map((rider) => (
                    <label
                      key={rider.id}
                      htmlFor={`erider-${rider.id}`}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors",
                        riderId === rider.id ? "border-primary bg-primary/5" : "border-border",
                      )}
                    >
                      <RadioGroupItem id={`erider-${rider.id}`} value={rider.id} />
                      <span className="min-w-0 flex-1 truncate text-sm">{rider.full_name}</span>
                      <Badge variant="outline" className="shrink-0 capitalize">
                        {rider.rider_rank}
                      </Badge>
                    </label>
                  ))}
                </div>
              </RadioGroup>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Required. A Session created without an owner would publish anonymous extra supply to
              every eligible Rider in the hub.
            </p>
          </fieldset>

          <fieldset>
            <legend className="mb-2 block text-sm font-medium">Maximum extension</legend>
            <div className="flex gap-2">
              {EXTENSION_CHOICES.map((hours) => (
                <Button
                  key={hours}
                  type="button"
                  variant={extension === hours ? "default" : "outline"}
                  onClick={() => setExtension(hours)}
                  className="flex-1"
                >
                  {hours}h
                </Button>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <label htmlFor="emergency-reason" className="block text-sm font-medium">
              Reason (required)
            </label>
            <Textarea
              id="emergency-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why does this date need supply generation did not produce?"
              rows={3}
            />
          </div>

          <Callout tone="info">
            This Session is marked as manually created for the rest of its life and is excluded from
            every generated-supply count. The week&rsquo;s locked Therapist count is not changed.
          </Callout>

          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                serviceDate: date,
                code,
                toRiderId: riderId,
                maxExtensionHours: extension,
                reason: trimmedReason,
              })
            }
          >
            {isPending ? "Creating…" : "Create manual Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
